// CASH1-S5 (docs/build-briefs/CASH1-S5-cash-settings.md; PRD P1.1; DEC-399): the rules behind the three drawer figures in
// Settings > Location > Cash. A draft of what was typed, turned into whole cents or into plain refusals. No DOM, so a node
// test proves it directly (test/cash1-s5-ui-settings-model.test.ts). The server still checks everything.

import { parseDollarsToCentsExact } from "./money.ts";

/** The server's own ceiling (src/config/cashDrawers.ts maxSettingCents), in cents. */
export const CASH_SETTING_MAX_CENTS = 10_000_000;

export type CashSettingField = "float" | "tolerance" | "paidOut";

export interface CashSettingDraft {
  float: string;
  tolerance: string;
  paidOut: string;
}

export interface CashSettingCents {
  standardFloatCents: number;
  toleranceCents: number;
  paidOutApprovalCents: number;
}

export type CashSettingErrors = Partial<Record<CashSettingField, string>>;

export type CashSettingParse =
  { ok: true; cents: CashSettingCents } | { ok: false; errors: CashSettingErrors };

/** 20000 -> "200.00". */
export function centsToDraftText(cents: number): string {
  return `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

export function draftFromCents(cents: CashSettingCents): CashSettingDraft {
  return {
    float: centsToDraftText(cents.standardFloatCents),
    tolerance: centsToDraftText(cents.toleranceCents),
    paidOut: centsToDraftText(cents.paidOutApprovalCents),
  };
}

export const CASH_SETTING_LABELS: Record<CashSettingField, string> = {
  float: "Starting float",
  tolerance: "Over or short allowed",
  paidOut: "Paid out needs a manager",
};

const MAX_TEXT = "$100,000.00";

function parseField(raw: string, label: string): { cents: number } | { error: string } {
  if (raw.trim().length === 0) return { error: `${label} needs an amount.` };
  try {
    const cents = parseDollarsToCentsExact(raw, label);
    if (cents > CASH_SETTING_MAX_CENTS) return { error: `${label} can be at most ${MAX_TEXT}.` };
    return { cents };
  } catch (error) {
    if (error instanceof RangeError) return { error: error.message };
    throw error;
  }
}

export function parseCashSettingDraft(draft: CashSettingDraft): CashSettingParse {
  const errors: CashSettingErrors = {};
  const out: Partial<Record<CashSettingField, number>> = {};
  for (const field of ["float", "tolerance", "paidOut"] as const) {
    const parsed = parseField(draft[field], CASH_SETTING_LABELS[field]);
    if ("error" in parsed) errors[field] = parsed.error;
    else out[field] = parsed.cents;
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    cents: {
      standardFloatCents: out.float as number,
      toleranceCents: out.tolerance as number,
      paidOutApprovalCents: out.paidOut as number,
    },
  };
}

/** True when the draft parses to something other than what the server holds. An unparseable draft counts as changed. */
export function draftIsChanged(draft: CashSettingDraft, held: CashSettingCents): boolean {
  const parsed = parseCashSettingDraft(draft);
  if (!parsed.ok) return true;
  return (
    parsed.cents.standardFloatCents !== held.standardFloatCents ||
    parsed.cents.toleranceCents !== held.toleranceCents ||
    parsed.cents.paidOutApprovalCents !== held.paidOutApprovalCents
  );
}
